import { faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'

const EmptyStateCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: min(100%, 440px);
  padding: 1.75rem;
  border: 1px solid rgba(12, 35, 64, 0.08);
  border-radius: 24px;
  overflow: hidden;
  background:
    radial-gradient(circle at top, rgba(255, 105, 0, 0.12), transparent 45%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 250, 0.98) 100%);
  box-shadow: 0 24px 60px rgba(12, 35, 64, 0.14);
`

const EmptyStateGlow = styled.div`
  position: absolute;
  inset: auto auto -80px -40px;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: rgba(12, 35, 64, 0.08);
  filter: blur(24px);
  pointer-events: none;
`

const EmptyStateBadge = styled.div`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  background: rgba(12, 35, 64, 0.08);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const EmptyStateContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  text-align: center;

  h3,
  p {
    margin: 0;
  }

  h3 {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 1.35rem;
    line-height: 1.1;
  }

  p {
    color: ${({ theme }) => theme.colors.darkGrey};
    line-height: 1.45;
  }

  strong {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
  }
`

const AskButton = styled.button`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  min-width: 230px;
  padding: 0.9rem 1.2rem;
  border: none;
  border-radius: 999px;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary} 0%,
    #1f4f8b 48%,
    ${({ theme }) => theme.colors.secondary} 100%
  );
  color: ${({ theme }) => theme.colors.lightText};
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 18px 34px rgba(12, 35, 64, 0.24);
  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    opacity 180ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px) scale(1.01);
    box-shadow: 0 22px 38px rgba(12, 35, 64, 0.28);
  }

  &:disabled {
    opacity: 0.72;
    cursor: wait;
  }
`

interface AskPrixAiEmptyStateProps {
  question: string
  onAsk: (question: string) => void | Promise<void>
  isLoading?: boolean
}

function formatQuestionPreview(question: string) {
  const normalizedQuestion = question.trim().replace(/\s+/g, ' ')
  if (normalizedQuestion.length <= 88) {
    return normalizedQuestion
  }

  return `${normalizedQuestion.slice(0, 85)}...`
}

export default function AskPrixAiEmptyState({
  question,
  onAsk,
  isLoading = false,
}: AskPrixAiEmptyStateProps) {
  const questionPreview = formatQuestionPreview(question)

  return (
    <EmptyStateCard>
      <EmptyStateGlow />

      <EmptyStateBadge>
        <FontAwesomeIcon icon={faWandMagicSparkles} />
        Prix IA
      </EmptyStateBadge>

      <EmptyStateContent>
        <h3>Sem resultados prontos</h3>
        <p>
          Não encontramos uma variável pronta para <strong>&quot;{questionPreview}&quot;</strong>.
          Transforme essa busca em uma pergunta para a Prix IA e receba ajuda para seguir.
        </p>
      </EmptyStateContent>

      <AskButton type='button' onClick={() => void onAsk(question)} disabled={isLoading}>
        <FontAwesomeIcon icon={faWandMagicSparkles} />
        {isLoading ? 'Abrindo Prix IA...' : 'Perguntar para a IA'}
      </AskButton>
    </EmptyStateCard>
  )
}
